import { Link } from "react-router-dom";
import { Check, ArrowRight, Sparkles } from "lucide-react";
import { MaskedLines, Reveal } from "@/components/Motion";
import { PRICING, CONTACT } from "@/data/site";

const INCLUDED = [
  "Fully equipped, police-checked cleaners",
  "Eco-friendly products on request",
  "Transparent, upfront pricing — no hidden fees",
  "100% satisfaction guarantee",
];

export default function Pricing() {
  return (
    <div>
      <section className="pt-40 pb-16 bg-[#FAFAF9]">
        <div className="max-w-[1440px] mx-auto px-5 sm:px-8">
          <p className="kicker mb-6">Pricing</p>
          <h1 className="font-display font-extrabold tracking-tight text-[#1C2A39] text-5xl sm:text-7xl leading-[0.95]">
            <MaskedLines lines={["Simple, honest", "pricing."]} />
          </h1>
          <Reveal delay={0.5}>
            <p className="mt-7 text-lg text-[#475569] max-w-xl">Residential cleans start from just $189. Commercial and bond cleans are quoted to your space — always upfront, never a surprise.</p>
          </Reveal>
        </div>
      </section>

      <section className="pb-24 bg-[#FAFAF9]">
        <div className="max-w-[1440px] mx-auto px-5 sm:px-8 grid md:grid-cols-3 gap-6 items-stretch">
          {PRICING.map((p, i) => (
            <Reveal key={p.key} delay={i * 0.1}>
              <div
                data-testid={`pricing-card-${p.key}`}
                className={`relative h-full rounded-3xl p-8 flex flex-col transition-[transform,box-shadow] duration-500 hover:-translate-y-1.5 ${
                  p.popular ? "bg-[#1C2A39] text-white shadow-[0_30px_70px_-30px_rgba(28,42,57,0.6)]" : "bg-white border border-slate-200 text-[#1C2A39]"
                }`}
              >
                {p.popular && (
                  <span className="absolute top-6 right-6 inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full bg-[#5EEAD4] text-[#1C2A39] font-semibold">
                    <Sparkles size={13} /> Most popular
                  </span>
                )}
                <h3 className={`font-display font-medium text-2xl ${p.popular ? "text-white" : "text-[#1C2A39]"}`}>{p.name}</h3>
                <div className="mt-5 flex items-end gap-2">
                  <span className="font-display font-extrabold text-5xl tracking-tight">{p.price}</span>
                  <span className={`text-sm mb-1.5 ${p.popular ? "text-slate-300" : "text-[#475569]"}`}>{p.unit}</span>
                </div>
                <p className={`mt-4 text-sm leading-relaxed ${p.popular ? "text-slate-300" : "text-[#475569]"}`}>{p.blurb}</p>
                <ul className="mt-7 space-y-3 flex-1">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-center gap-3 text-sm">
                      <Check size={16} className={p.popular ? "text-[#5EEAD4]" : "text-[#0D9488]"} /> {f}
                    </li>
                  ))}
                </ul>
                <Link
                  to="/quote"
                  data-testid={`pricing-cta-${p.key}`}
                  className={`mt-8 justify-center ${p.popular ? "btn-primary !bg-white !text-[#1C2A39] hover:!bg-[#5EEAD4]" : "btn-primary"}`}
                >
                  {p.cta} <ArrowRight size={17} />
                </Link>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="py-24 bg-white border-t border-slate-100">
        <div className="max-w-[1440px] mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-14 items-center">
          <div>
            <p className="kicker mb-4">Always included</p>
            <h2 className="font-display font-medium text-4xl sm:text-5xl tracking-tight text-[#1C2A39] leading-[1.05]">Every clean, every price.</h2>
            <ul className="mt-8 space-y-4">
              {INCLUDED.map((f) => (
                <li key={f} className="flex items-center gap-3 text-[#1C2A39]"><Check size={18} className="text-[#0D9488]" /> {f}</li>
              ))}
            </ul>
          </div>
          <div className="bg-[#E8F1EB] rounded-3xl p-10">
            <h3 className="font-display font-medium text-3xl text-[#1C2A39]">Need a tailored quote?</h3>
            <p className="mt-4 text-[#475569]">Bond cleans, offices and larger properties are priced to your exact needs. Tell us about your space and we'll get back fast.</p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Link to="/quote" className="btn-primary">Get a Free Quote <ArrowRight size={17} /></Link>
              <a href={`tel:${CONTACT.phoneRaw}`} className="btn-ghost">Call {CONTACT.phone}</a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
